import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useForm from '../../hooks/useForm';
import { getDashboardPath } from '../../utils/auth';


import logo from '../../assets/images/Bholi ko itahari .png'; 

// ─── Validation ─────────────────────────────────────────────────────────────
const validate = (values) => {
  const errors = {};
  if (!values.email)                            errors.email = 'Email is required';
  else if (!/\S+@\S+\.\S+/.test(values.email)) errors.email = 'Enter a valid email address';
  if (!values.password)                         errors.password = 'Password is required';
  return errors;
};

// ─── Styles object ───────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F5F3EE',
    padding: '2rem 1rem',
  },
  card: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
    maxWidth: '900px',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 12px 48px rgba(0,0,0,0.14)',
  },

  // ── Left panel ──
  leftPanel: {
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImg: {
    width: '200px',
    height: 'auto',
    display: 'block',
  },

  // ── Right panel ──
  rightPanel: {
    background: '#0a5c2e',
    padding: '3rem 2.5rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  formWrap: {
    maxWidth: '360px',
    width: '100%',
    margin: '0 auto',
  },
  formTitle: {
    fontSize: '1.55rem',
    fontWeight: 700,
    color: '#ffffff',
    margin: '0 0 0.4rem 0',
  },
  formSub: {
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.7)',
    margin: '0 0 1.75rem 0',
  },

  // ── Alert ──
  alert: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.85rem',
    marginBottom: '1rem',
    background: '#FEE2E2',
    color: '#991B1B',
    border: '1px solid #FECACA',
  },

  // ── Form group ──
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.65rem 0.85rem',
    fontSize: '0.875rem',
    border: '1.5px solid rgba(255,255,255,0.3)',
    borderRadius: '8px',
    color: '#111',
    background: '#ffffff',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  errorMsg: {
    display: 'block',
    fontSize: '0.78rem',
    color: '#DC2626',
    marginTop: '0.3rem',
  },

  // ── Submit button ──
  btn: {
    width: '100%',
    marginTop: '1.5rem',
    padding: '0.8rem',
    background: '#ffffff',
    color: '#0a5c2e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    letterSpacing: '0.01em',
  },
  btnDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },

  // ── Spinner ──
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },

  // ── Footer text ──
  footerText: {
    textAlign: 'center',
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
    marginTop: '1.25rem',
  },
  footerLink: {
    color: '#ffffff',
    fontWeight: 700,
    textDecoration: 'underline',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────
const Login = () => {
  const { login }  = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const from       = location.state?.from?.pathname || '/';

  const [serverError, setServerError] = useState('');
  const [loading, setLoading]         = useState(false);

  const { values, errors, touched, handleChange, handleBlur, validateAll } = useForm(
    { email: '', password: '' },
    validate
  );

  const handleSubmit = async (e) => {
  e.preventDefault();
  setServerError('');
  if (!validateAll()) return;
  setLoading(true);
  try {
    const loggedInUser = await login(values.email, values.password);

    const cameFromSpecificPage = Boolean(location.state?.from?.pathname);
    const destination = cameFromSpecificPage
      ? from
      : getDashboardPath(loggedInUser?.role);

    navigate(destination, { replace: true });
  } catch (err) {
    setServerError(err.response?.data?.message || 'Login failed. Please try again.');
  } finally {
    setLoading(false);
  }
};
  return (
    <>
      {/* Keyframe for spinner — injected once via a <style> tag */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={S.page}>
        <div style={S.card}>

          {/* ── Left panel ── */}
          <div style={S.leftPanel}>
            <img src={logo} alt="Bholi ko itahari" style={S.logoImg} />
          </div>

          {/* ── Right panel ── */}
          <div style={S.rightPanel}>
            <div style={S.formWrap}>
              <h2 style={S.formTitle}>Welcome back</h2>
              <p style={S.formSub}>Sign in to your account to continue</p>

              {serverError && (
                <div style={S.alert} role="alert">{serverError}</div>
              )}

              <form onSubmit={handleSubmit} noValidate>

                {/* Email */}
                <div style={S.formGroup}>
                  <label style={S.label} htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    style={{
                      ...S.input,
                      ...(touched.email && errors.email ? S.inputError : {}),
                    }}
                    placeholder="you@example.com"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="email"
                  />
                  {touched.email && errors.email && (
                    <span style={S.errorMsg} role="alert">{errors.email}</span>
                  )}
                </div>

                {/* Password */}
                <div style={S.formGroup}>
                  <label style={S.label} htmlFor="password">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    style={{
                      ...S.input,
                      ...(touched.password && errors.password ? S.inputError : {}),
                    }}
                    placeholder="••••••••"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="current-password"
                  />
                  {touched.password && errors.password && (
                    <span style={S.errorMsg} role="alert">{errors.password}</span>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div style={S.spinner} />
                      Signing in…
                    </>
                  ) : 'Sign In'}
                </button>
              </form>

              <p style={S.footerText}>
                Want to become a reporter?{' '}
                <Link to="/register" style={S.footerLink}>Register here</Link>
              </p>

            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Login;
